<template>
  <transition name="fade" appear>
    <div
      v-if="open"
      class="left-0 top-0 flex items-center justify-center z-50 bg-n-950/50 backdrop-blur-sm sm:py-4"
      :class="[
        isScreenFixed ? 'fixed w-screen h-screen' : 'absolute inset-0',
        { 'cursor-pointer': backdropClosable }
      ]"
      @click="backdropClosable && emit('close')"
    >
      <transition name="scale" appear>
        <div
          class="flex flex-col bg-n-0 dark:bg-n-900 w-full h-full sm:max-h-full shadow-lg sm:rounded-lg text-n-800 dark:text-n-0 relative overflow-hidden pb-safe sm:pb-0 cursor-default"
          :class="{
            'sm:w-[32rem] sm:max-h-[48rem]': size === 'small',
            'sm:w-[60rem] sm:max-h-[35rem]': size === 'large',
            'sm:h-auto': !fullHeight
          }"
          @click.stop
        >
          <template v-if="renderMode === 'props' || renderMode === 'named-slots'">
            <slot v-if="slots.header" name="header" />
            <l-modal-header
              v-else-if="props.title || props.showClose"
              :title="props.title"
              :show-close="props.showClose"
              @close="emit('close')"
            />
          </template>

          <template v-if="renderMode === 'props' || renderMode === 'named-slots'">
            <div v-if="props.description" class="flex-none px-4 sm:px-6 font-body mb-2">
              {{ props.description }}
            </div>
            <l-modal-content>
              <slot />
            </l-modal-content>
          </template>
          <template v-else-if="renderMode === 'components'">
            <slot />
          </template>

          <template v-if="renderMode === 'props'">
            <l-modal-footer v-if="slots.footer">
              <slot name="footer" />
            </l-modal-footer>
            <l-modal-footer
              v-else-if="
                (props.buttonText && props.button) ||
                (props.secondaryButtonText && props.secondaryButton)
              "
            >
              <div
                class="grid gap-2"
                :class="[props.button && props.secondaryButton ? 'grid-cols-2' : 'grid-cols-1']"
              >
                <l-button
                  v-if="props.secondaryButtonText && props.secondaryButton"
                  class="max-w-72 w-full h-12"
                  :class="{ 'mx-auto': !(props.button && props.buttonText) }"
                  :size="props.secondaryButtonSize"
                  :type="props.secondaryButtonType"
                  :disabled="!props.secondaryButtonEnabled"
                  :loading="props.secondaryButtonLoading"
                  @click="emit('secondaryAction')"
                >
                  {{ props.secondaryButtonText }}
                </l-button>
                <l-button
                  v-if="props.buttonText && props.button"
                  class="max-w-72 w-full h-12"
                  :class="{
                    'mx-auto': !(props.secondaryButtonText && props.secondaryButton)
                  }"
                  :size="props.buttonSize"
                  :type="props.buttonType"
                  :disabled="!props.buttonEnabled"
                  :loading="props.buttonLoading"
                  @click="emit('action')"
                >
                  {{ props.buttonText }}
                </l-button>
              </div>
            </l-modal-footer>
          </template>
          <template v-else-if="renderMode === 'named-slots'">
            <l-modal-footer v-if="slots.footer">
              <slot name="footer" />
            </l-modal-footer>
          </template>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, provide, useSlots } from 'vue'

import LButton from '../LButton.vue'
import LModalContent from './LModalContent.vue'
import LModalFooter from './LModalFooter.vue'
import LModalHeader from './LModalHeader.vue'

type Props = {
  /**
   * Whether the modal is open or closed
   * @default true
   */
  open?: boolean
  /**
   * Size of the modal
   * @default smalls
   */
  size?: 'small' | 'large'
  /**
   * Whether the modal should take the full height of the screen
   * @default false
   */
  fullHeight?: boolean
  isScreenFixed?: boolean
  /**
   * Whether clicking the backdrop should close the modal
   * @default false
   */
  backdropClosable?: boolean
  /**
   * Whether to show the close button in the header
   * @default true
   */
  showClose?: boolean
  /**
   * Title of the modal
   */
  title?: string
  /**
   * Description text below the title
   */
  description?: string
  /**
   * Show buttons on the footer
   * @default true
   */
  button?: boolean
  buttonText?: string
  buttonEnabled?: boolean
  buttonSize?: 'small' | 'medium' | 'large'
  buttonType?: 'primary' | 'secondary' | 'link' | 'text'
  buttonLoading?: boolean
  secondaryButton?: boolean
  secondaryButtonText?: string
  secondaryButtonEnabled?: boolean
  secondaryButtonSize?: 'small' | 'medium' | 'large'
  secondaryButtonType?: 'primary' | 'secondary' | 'link' | 'text'
  secondaryButtonLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  open: true,
  size: 'small',
  fullHeight: false,
  isScreenFixed: true,
  backdropClosable: false,
  showClose: true,
  //
  title: '',
  description: '',
  //
  button: true,
  buttonText: '',
  buttonEnabled: true,
  buttonSize: 'small',
  buttonType: 'primary',
  buttonLoading: false,
  secondaryButton: false,
  secondaryButtonText: '',
  secondaryButtonEnabled: true,
  secondaryButtonSize: 'small',
  secondaryButtonType: 'secondary',
  secondaryButtonLoading: false
})

const emit = defineEmits<{
  action: []
  secondaryAction: []
  close: []
}>()

const slots = useSlots()

provide('modalContext', {
  close: () => emit('close')
})

/**
 * Determine the render mode based on the provided props and slots
 * Used to maintain backward compatibility while supporting new use cases
 * - 'props': when title/description/button props are used
 * - 'named-slots': when named slots (header/footer) are used
 * - 'components': when default slot is used for custom components
 */
const renderMode = computed(() => {
  const hasPropsConfig = !!(props.title || props.buttonText || props.secondaryButtonText)
  const hasNamedSlots = !!(slots.header || slots.footer)
  const hasDefaultSlot = !!slots.default

  if (hasPropsConfig) return 'props'
  if (hasNamedSlots) return 'named-slots'
  if (hasDefaultSlot) return 'components'
  return 'props'
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.scale-enter-active,
.scale-leave-active {
  transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.scale-enter-from,
.scale-leave-to {
  transform: scale(0.95);
}

.scale-enter-to,
.scale-leave-from {
  transform: scale(1);
}
</style>
